import { useNavigate, useSearchParams } from '@solidjs/router'
import { createEffect, createMemo, createResource, Show, Suspense } from 'solid-js'
import AlertBag from '../../components/AlertBag'
import GamePlayThreeJsCanvas from '../../components/GamePlayThreeJsCanvasV2'
import Loader from '../../components/Loader'
import { ensureStateMapDisk } from '../../lib/core/state/state_map_disk.helpers'
import { StateMapDisk } from '../../lib/core/state/state_map_disk.enum'
import { StateSchema } from '../../lib/core/state/State.type'
import { StateType } from '../../lib/core/state/state_type.enum'
import parseIntOrNull from '../../lib/core/util/math/parseIntOrNull'
import { parse } from 'valibot'
import { gameDisk } from '../../lib/core/disk/IndexedDBDisk'
import { hasIndexedDB } from '../../lib/core/Storage/IndexedDB/hasIndexedDB'
import createRedirectIfWrongStateTypeEffect from '../../rx/effect/createRedirectIfWrongStateTypeEffect'
import createGameRowResource from '../../rx/resource/createGameRowResource'
import { createLoadingSignal, registerLoadingResource } from '../../rx/signal/create_loading_signal'
import { createAlertBagStore, registerAlertBagResource } from '../../rx/store/AlertBag'
import type { Nullable } from '../../typescript'

type GamePlayPageSearchParams = {
  disk: string
  game_id: string
}

const componentStateType = StateType.Game

const GamePlayPage = () => {
  const [searchParams] = useSearchParams<GamePlayPageSearchParams>()
  const navigate = useNavigate()

  const loading = createLoadingSignal()
  const ab = createAlertBagStore()
  const supportsIndexedDB = hasIndexedDB()

  const disk = createMemo(() => ensureStateMapDisk(searchParams.disk))
  const stateId = createMemo<Nullable<number>>((): Nullable<number> => parseIntOrNull(searchParams.game_id))

  const isOfflineDisk = createMemo(() => disk() === StateMapDisk.IndexedDB)
  createEffect(() => {
    if (isOfflineDisk() && !supportsIndexedDB) {
      throw new Error('IndexedDB disk requested but not supported')
    }
  })
  const offlineGameResource = createResource(
    () => (isOfflineDisk() ? stateId() : null),
    async (id) => {
      if (!id) {
        return null
      }
      if (!supportsIndexedDB) {
        throw new Error('IndexedDB not available for offline game loading')
      }
      const row = await gameDisk.load(id)
      if (row) {
        row.data = parse(StateSchema, row.data)
      }
      return row
    }
  )

  registerLoadingResource(loading, offlineGameResource)
  registerAlertBagResource(ab, offlineGameResource)

  createEffect(() => {
    const currentDisk = disk()
    const id = stateId()
    if (currentDisk === StateMapDisk.IndexedDB) {
      if (!id) {
        navigate('/game')
      }
    } else if (currentDisk === StateMapDisk.Cloud) {
      if (!id) {
        navigate('/game')
      }
    } else {
      throw new Error('Invalid disk: ' + currentDisk)
    }
  })

  const remoteStateId = createMemo<Nullable<number>>(() => (isOfflineDisk() ? null : stateId()))
  // eslint-disable-next-line solid/reactivity
  const stateItemResource = createGameRowResource(remoteStateId)
  const [stateRow] = stateItemResource
  createRedirectIfWrongStateTypeEffect(componentStateType, () => stateRow()?.data?.type, () => stateRow()?.data?.online?.id)

  registerLoadingResource(loading, stateItemResource)
  registerAlertBagResource(ab, stateItemResource)

  return (
    <div id="GamePlayPage">
      <Loader signal={loading} />
      <AlertBag store={ab} />
      <Suspense fallback={<div class="text-muted">Loading...</div>}>
        <Show when={isOfflineDisk()}>
          <Show when={offlineGameResource[0]()} fallback={<div class="text-muted">Offline game not found.</div>}>
            {(row) => <GamePlayThreeJsCanvas row={row()} local />}
          </Show>
        </Show>
        <Show when={!isOfflineDisk()}>
          <Show when={stateRow()} children={(sr) => <GamePlayThreeJsCanvas row={sr()} />} />
        </Show>
      </Suspense>
    </div>
  )
}

export default GamePlayPage
